/**
 * ============================================================================
 * FAMILY MEMBER LIST v1.0.0 - Linke Seite mit Mitglieder-Liste
 * ============================================================================
 *
 * Zeigt die Liste aller Familienmitglieder an:
 * - Klickbare Mitglieder-Karten
 * - "+ Mitglied hinzufügen" Button
 * - Ausgewähltes Mitglied hervorgehoben
 *
 * @version 1.0.0
 * @date 2026-01-16
 * ============================================================================
 */

import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import FamilyMemberCard from './FamilyMemberCard';

console.log('👥 FamilyMemberList.js v1.0.0 loaded');

/**
 * FamilyMemberList Component
 */
function FamilyMemberList({
  members = [],
  selectedMemberId,
  onSelectMember,
  onAddMember,
  onInviteWithCode,
  loading = false
}) {
  const { isDark } = useTheme();
  const styles = getStyles(isDark);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>
          <span>👨‍👩‍👧‍👦</span> Familie
        </h3>
        <span style={styles.count}>{members.length}</span>
      </div>

      {/* Members List */}
      <div style={styles.list}>
        {loading ? (
          // Skeleton Loading
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} style={styles.skeleton} />
            ))}
          </>
        ) : members.length === 0 ? (
          // Empty State
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>👤</span>
            <p style={styles.emptyText}>Keine Mitglieder</p>
          </div>
        ) : (
          // Member Cards
          members.map((member) => (
            <FamilyMemberCard
              key={member.id}
              member={member}
              isSelected={selectedMemberId === member.id}
              onClick={() => onSelectMember?.(member)}
            />
          ))
        )}
      </div>

      {/* Action Buttons */}
      <div style={styles.buttonContainer}>
        {onAddMember && (
          <button
            style={styles.addButton}
            onClick={onAddMember}
            type="button"
          >
            <span style={styles.addIcon}>+</span>
            <span>Mitglied hinzufügen</span>
          </button>
        )}
        {onInviteWithCode && (
          <button
            style={styles.inviteButton}
            onClick={onInviteWithCode}
            type="button"
          >
            <span>🔗</span>
            <span>Mit Code einladen</span>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Styles
 */
function getStyles(isDark) {
  const colors = isDark ? {
    bg: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  } : {
    bg: '#FFFFFF',
    bgSecondary: '#E8F4F8',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  };

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: colors.bg,
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      overflow: 'hidden'
    },

    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderBottom: `1px solid ${colors.border}`,
      background: colors.bgSecondary
    },

    title: {
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.text
    },

    count: {
      background: colors.accent,
      color: 'white',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.85rem',
      fontWeight: 600
    },

    list: {
      flex: 1,
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      overflowY: 'auto',
      minHeight: 0
    },

    skeleton: {
      height: '72px',
      background: isDark ? '#374151' : '#E8F4F8',
      borderRadius: '12px',
      animation: 'pulse 1.5s infinite'
    },

    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      color: colors.textMuted
    },

    emptyIcon: {
      fontSize: '3rem',
      marginBottom: '12px',
      opacity: 0.5
    },

    emptyText: {
      margin: 0,
      fontSize: '0.9rem'
    },

    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '12px'
    },

    addButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },

    inviteButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      background: colors.bg,
      color: colors.accent,
      border: `2px solid ${colors.accent}`,
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },

    addIcon: {
      fontSize: '1.2rem',
      fontWeight: 700
    }
  };
}

export default FamilyMemberList;
